/**
 * rebuy-cart-bridge.js — Londontown
 * ------------------------------------------------------------------
 * Single source of truth for "reveal the cart after an add-to-cart".
 *
 * WHY: the theme opened the native drawer only `if (!window.Rebuy)` and had no
 * Smart Cart fallback. In the window where the SDK is loaded but Smart Cart isn't
 * `ready` yet, an add succeeded but NOTHING opened (native suppressed + SC not ready)
 * — the "click 5x, 1 item, nothing slides out" bug. This bridge guarantees an add is
 * never silent: show Smart Cart when ready, otherwise fall back to the native drawer.
 *
 * Uses ONLY documented Rebuy APIs: Rebuy.SmartCart.show()/.status, Rebuy.Cart.fetchCart().
 * Safe when Rebuy is absent (embed disabled / no preview): every branch falls through
 * to the native drawer, i.e. current live behavior is preserved untouched.
 */
(function () {
  'use strict';

  var REVEAL_DEBOUNCE_MS = 150;   // collapse the multiple events one add emits
  var READY_TIMEOUT_MS  = 1500;   // how long to wait for Smart Cart before native fallback
  var READY_POLL_MS     = 100;

  var suppressReveal = false;     // guard against the native-fallback re-triggering us
  var revealTimer = null;

  function rebuyPresent() {
    return !!window.Rebuy;
  }
  function smartCartReady() {
    try {
      return !!(window.Rebuy && window.Rebuy.SmartCart && window.Rebuy.SmartCart.status === 'ready');
    } catch (e) { return false; }
  }

  function showSmartCart() {
    try {
      // Re-sync first — required for XHR (jQuery $.ajax) adds that the fetch-wrapper never saw.
      if (window.Rebuy && window.Rebuy.Cart && typeof window.Rebuy.Cart.fetchCart === 'function') {
        window.Rebuy.Cart.fetchCart();
      }
    } catch (e) {}
    try {
      window.Rebuy.SmartCart.show();
    } catch (e) {
      // If show() ever throws, don't leave the customer with nothing.
      nativeOpen();
    }
  }

  // Open the theme's native Impulse drawer via its documented custom events.
  function nativeOpen() {
    suppressReveal = true;
    document.dispatchEvent(new CustomEvent('cart:build')); // refresh contents (theme.CartForm listens)
    document.dispatchEvent(new CustomEvent('cart:open'));  // open drawer (theme.CartDrawer listens)
    setTimeout(function () { suppressReveal = false; }, 300);
  }

  // Poll for Smart Cart readiness, then run showFn; on timeout run fallbackFn.
  // Poll-based (not event-name-dependent) so it survives SDK event renaming.
  function waitForReadyThen(showFn, fallbackFn) {
    var start = Date.now();
    (function check() {
      if (smartCartReady()) return showFn();
      if (Date.now() - start >= READY_TIMEOUT_MS) return fallbackFn();
      setTimeout(check, READY_POLL_MS);
    })();
  }

  // Decide how to reveal the cart after an add.
  function revealCart() {
    if (suppressReveal) return;
    if (smartCartReady()) { showSmartCart(); return; }
    if (rebuyPresent())  { waitForReadyThen(showSmartCart, nativeOpen); return; }
    nativeOpen();
  }

  function revealCartDebounced() {
    if (suppressReveal) return;
    clearTimeout(revealTimer);
    revealTimer = setTimeout(revealCart, REVEAL_DEBOUNCE_MS);
  }

  // All 8 ATC paths emit one of these. Centralizing here means we don't edit each site.
  ['ajaxProduct:added', 'cart:refresh', 'cart:build'].forEach(function (ev) {
    document.addEventListener(ev, revealCartDebounced);
  });

  // Cart-icon interceptor (capture phase) — fixes the Smart Cart V2 launch race where a
  // cart-icon click before `ready` is swallowed. Only intercepts when Rebuy is present.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('.js-drawer-open-cart, .cart-link, [data-rebuy-cart-toggle]');
    if (!trigger) return;
    if (!rebuyPresent()) return;             // Rebuy off → let the native drawer handle it
    e.preventDefault();
    e.stopImmediatePropagation();
    if (smartCartReady()) { showSmartCart(); return; }
    waitForReadyThen(showSmartCart, nativeOpen); // pre-ready: gate then SC, else native (never swallow)
  }, true);
})();
